/* Cart shipping presentation and live refresh using the app's configured threshold. */
(() => {
  if (window.rmcfCartShipping || !document.body.classList.contains('template-cart')) return;
  window.rmcfCartShipping = true;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  const previousValues = new Map();
  const progressStates = new Map();
  let previousQualified = false;
  let pendingQualificationBurst = false;
  let qualificationTimer = 0;
  let confettiFrame = 0;
  let confettiOverlay = null;
  let confettiTimer = 0;
  const selector = '.xaway-free-shipping-text .app-cart-amount-price';

  function currency(text) {
    // The app uses en-US Intl currency formatting. Retain its exact prefix/suffix.
    const match = text.match(/^(.*?)(-?\d[\d,]*\.\d{2})(.*?)$/);
    if (!match) return null;
    const cents = Math.round(Number(match[2].replaceAll(',', '')) * 100);
    if (!Number.isSafeInteger(cents)) return null;
    return { cents, format: value => match[1] + (value / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }) + match[3] };
  }

  function sizeMessage(message) {
    const nodes = [message, ...message.querySelectorAll('*')].filter(node =>
      !node.closest('.rmcf-shipping-amount') && !node.classList.contains('rmcf-shipping-text'));
    if (!nodes.length) return;
    // Newly inserted app children must be measured without already-scaled ancestors.
    const sized = [message, ...message.querySelectorAll('.rmcf-shipping-text')]
      .filter(node => node.classList.contains('rmcf-shipping-text'));
    sized.forEach(node => node.classList.remove('rmcf-shipping-text'));
    const sizes = nodes.map(node => getComputedStyle(node).fontSize);
    sized.forEach(node => node.classList.add('rmcf-shipping-text'));
    nodes.forEach((node, index) => {
      node.style.setProperty('--rmcf-shipping-font-size', sizes[index]);
      node.classList.add('rmcf-shipping-text');
    });
  }

  function stop(state) {
    cancelAnimationFrame(state.frame);
    state.frame = 0;
  }

  function update(source) {
    const message = source.closest('.xaway-free-shipping-text');
    sizeMessage(message);
    const finalText = source.textContent;
    const value = currency(finalText);
    let state = states.get(source);
    if (!value) {
      if (state) { stop(state); state.visual.textContent = finalText; state.text = finalText; }
      return;
    }
    if (!state) {
      const shell = document.createElement('span');
      shell.className = 'rmcf-shipping-amount rmcf-shipping-text';
      shell.style.setProperty('--rmcf-shipping-font-size', source.style.getPropertyValue('--rmcf-shipping-font-size'));
      shell.style.color = getComputedStyle(source).color;
      const visual = document.createElement('span');
      visual.className = 'rmcf-shipping-amount-visual';
      visual.setAttribute('aria-hidden', 'true');
      source.before(shell);
      shell.append(source, visual);
      source.classList.add('rmcf-shipping-amount-source');
      const wrapper = source.closest('.xaway-wrapper');
      const key = wrapper?.getAttribute('data-id') || wrapper?.id ||
        String([...document.querySelectorAll(selector)].indexOf(source));
      state = { shell, visual, key, displayed: Math.max(0, previousValues.get(key) ?? 0), text: null, frame: 0, width: 0 };
      states.set(source, state);
    }
    const hidden = message.classList.contains('powerup-n');
    if (state.text === finalText && state.hidden === hidden) return;
    const previous = state.displayed;
    stop(state);
    state.text = finalText;
    state.hidden = hidden;
    state.final = value.cents;
    state.visual.textContent = finalText;
    // Reserve the widest value encountered; frame updates never change line wrapping.
    const fontSize = parseFloat(getComputedStyle(state.shell).fontSize);
    const range = document.createRange();
    range.selectNodeContents(state.visual);
    const finalWidth = range.getBoundingClientRect().width / fontSize;
    state.visual.textContent = value.format(previous);
    range.selectNodeContents(state.visual);
    state.width = Math.max(state.width, source.getBoundingClientRect().width / fontSize,
      finalWidth, range.getBoundingClientRect().width / fontSize);
    state.visual.textContent = finalText;
    state.shell.style.minWidth = `${state.width}em`;
    if (motion.matches || hidden || document.hidden || previous === value.cents) {
      state.displayed = value.cents;
      return;
    }
    const start = performance.now();
    const tick = now => {
      if (!source.isConnected) { stop(state); return; }
      const progress = Math.min(1, (now - start) / 700);
      state.displayed = Math.round(previous + (value.cents - previous) * (1 - (1 - progress) ** 3));
      state.visual.textContent = progress === 1 ? finalText : value.format(state.displayed);
      state.frame = progress < 1 ? requestAnimationFrame(tick) : 0;
    };
    state.visual.textContent = value.format(previous);
    state.frame = requestAnimationFrame(tick);
  }

  function decorateBenefits() {
    document.querySelectorAll('.xaway-free-shipping-text, .sucess-free-shipping-text').forEach(message => {
      sizeMessage(message);
      const walker = document.createTreeWalker(message, NodeFilter.SHOW_TEXT, {
        acceptNode: node => node.parentElement.closest('.rmcf-shipping-benefit, .rmcf-shipping-amount')
          ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
      });
      const matches = [];
      while (walker.nextNode()) {
        if (/free 2nd day shipping/i.test(walker.currentNode.textContent)) matches.push(walker.currentNode);
      }
      matches.forEach(node => {
        const fragment = document.createDocumentFragment();
        const parts = node.textContent.split(/(free 2nd day shipping)/ig);
        parts.forEach(part => {
          if (!/^free 2nd day shipping$/i.test(part)) {
            fragment.append(document.createTextNode(part));
            return;
          }
          const benefit = document.createElement('strong');
          benefit.className = 'rmcf-shipping-benefit';
          benefit.textContent = part;
          const shimmer = document.createElement('span');
          shimmer.className = 'rmcf-shipping-benefit-shimmer';
          shimmer.setAttribute('aria-hidden', 'true');
          shimmer.setAttribute('data-label', part);
          benefit.append(shimmer);
          fragment.append(benefit);
        });
        node.replaceWith(fragment);
      });
    });
  }

  function finishProgress(state) {
    cancelAnimationFrame(state.frame);
    state.frame = 0;
    if (state.bar.classList.contains('rmcf-shipping-progress-entering')) {
      // Settle on the latest app target before restoring its normal width transition.
      state.bar.style.setProperty('--rmcf-shipping-progress-width', state.bar.style.width || 'auto');
      state.bar.getBoundingClientRect();
      state.bar.classList.remove('rmcf-shipping-progress-entering');
    }
    state.bar.style.removeProperty('--rmcf-shipping-progress-width');
  }

  function syncProgress() {
    progressStates.forEach((state, bar) => {
      if (!bar.isConnected) { finishProgress(state); progressStates.delete(bar); }
    });
    document.querySelectorAll('.xaway-progress > .xaway-progress-bar').forEach(bar => {
      const track = bar.parentElement;
      const knob = bar.querySelector('.xaway-icon');
      let state = progressStates.get(bar);
      if (state) {
        if (state.width !== bar.style.width || !knob || !track.getClientRects().length || !bar.getClientRects().length) finishProgress(state);
        state.width = bar.style.width;
        return;
      }
      if (!knob || !track.getClientRects().length || !bar.getClientRects().length) return;
      state = { bar, width: bar.style.width, frame: 0 };
      progressStates.set(bar, state);
      if (motion.matches || document.hidden) return;
      // Temporarily disable the app's width transition; its inline target is untouched.
      bar.style.setProperty('--rmcf-shipping-progress-width', bar.style.width || getComputedStyle(bar).width);
      bar.classList.add('rmcf-shipping-progress-entering');
      const width = bar.getBoundingClientRect().width;
      if (width <= 0) { finishProgress(state); return; }
      // Animate a presentation-only CSS variable, leaving the app's inline width intact.
      const started = performance.now();
      bar.style.setProperty('--rmcf-shipping-progress-width', '0px');
      const tick = now => {
        if (!bar.isConnected || state.width !== bar.style.width || motion.matches || document.hidden) {
          finishProgress(state);
          return;
        }
        const progress = Math.min(1, (now - started) / 1100);
        // Solve the approved cubic-bezier(0.16, 1, 0.3, 1) curve for this time.
        let low = 0;
        let high = 1;
        for (let i = 0; i < 16; i += 1) {
          const t = (low + high) / 2;
          const x = 3 * (1 - t) ** 2 * t * 0.16 + 3 * (1 - t) * t ** 2 * 0.3 + t ** 3;
          if (x < progress) low = t; else high = t;
        }
        const eased = 1 - (1 - (low + high) / 2) ** 3;
        bar.style.setProperty('--rmcf-shipping-progress-width', `${width * eased}px`);
        if (progress < 1) state.frame = requestAnimationFrame(tick);
        else finishProgress(state);
      };
      state.frame = requestAnimationFrame(tick);
    });
  }

  function clearConfetti() {
    clearTimeout(confettiTimer);
    confettiOverlay?.querySelectorAll('span').forEach(piece =>
      piece.getAnimations().forEach(animation => animation.cancel()));
    confettiOverlay?.remove();
    confettiOverlay = null;
  }

  function burstConfetti(knob) {
    const rect = knob.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    // Only celebrate a visible, on-screen knob; never replay on later cart changes.
    if (!rect.width || !rect.height || x < 0 || x > innerWidth || y < 0 || y > innerHeight) return;
    confettiOverlay = document.createElement('div');
    confettiOverlay.className = 'rmcf-shipping-confetti';
    confettiOverlay.setAttribute('aria-hidden', 'true');
    confettiOverlay.style.setProperty('--rmcf-confetti-x', `${x}px`);
    confettiOverlay.style.setProperty('--rmcf-confetti-y', `${y}px`);
    const colors = ['#707852', '#faf5f1'];
    const pieceCount = 96;
    for (let i = 0; i < pieceCount; i += 1) {
      const piece = document.createElement('span');
      piece.style.backgroundColor = colors[i % colors.length];
      if (i % 4 === 0) piece.style.borderRadius = '50%';
      confettiOverlay.append(piece);
    }
    document.body.append(confettiOverlay);
    confettiOverlay.querySelectorAll('span').forEach((piece, i) => {
      const angle = -Math.PI + (i + Math.random()) / pieceCount * Math.PI;
      const vx = Math.cos(angle) * (260 + Math.random() * 80);
      const vy = Math.sin(angle) * (220 + Math.random() * 60) - 70;
      const rotation = (Math.random() - 0.5) * 650;
      const flutter = 8 + Math.random() * 10;
      const phase = Math.random() * Math.PI * 2;
      const turns = 6 + Math.random() * 5;
      // Dense curved samples give the burst drag, gravity and gentle paper flutter.
      const frames = Array.from({ length: 41 }, (_, frame) => {
        const t = frame / 40;
        const drift = vx * (1 - Math.exp(-1.7 * t)) / 1.7;
        const sway = flutter * Math.sin(turns * t + phase) * t * t;
        const riseAndFall = vy * t + 340 * t * t;
        const tilt = Math.sin(turns * t + phase) * 65;
        return {
          offset: t,
          transform: `translate(${drift + sway}px, ${riseAndFall}px) rotate(${rotation * t}deg) rotateY(${tilt}deg)`,
          opacity: t <= 0.7 ? 1 : Math.max(0, 1 - ((t - 0.7) / 0.3) ** 1.5)
        };
      });
      piece.animate(frames, { duration: 1400 + Math.random() * 350, easing: 'linear', fill: 'forwards' });
    });
    confettiTimer = setTimeout(clearConfetti, 1850);
  }

  function cancelQualificationBurst() {
    cancelAnimationFrame(confettiFrame);
    confettiFrame = 0;
    clearTimeout(qualificationTimer);
    qualificationTimer = 0;
    pendingQualificationBurst = false;
    clearConfetti();
  }

  function checkQualification() {
    if (confettiFrame) return;
    confettiFrame = requestAnimationFrame(() => {
      confettiFrame = 0;
      const visible = element => element && element.getClientRects().length &&
        !element.classList.contains('powerup-n') && getComputedStyle(element).visibility !== 'hidden';
      const qualified = [...document.querySelectorAll('.sucess-free-shipping-text')].find(visible);
      const nowQualified = Boolean(qualified);
      if (!nowQualified) {
        previousQualified = false;
        cancelQualificationBurst();
        return;
      }
      if (!previousQualified) pendingQualificationBurst = true;
      previousQualified = true;
      if (!pendingQualificationBurst || qualificationTimer) return;
      if (motion.matches || document.hidden) { pendingQualificationBurst = false; return; }
      const knob = qualified.closest('.xaway-wrapper')?.querySelector('.xaway-progress-bar .xaway-icon');
      if (!visible(knob)) return;
      const bar = knob.parentElement;
      if (progressStates.get(bar)?.frame) { checkQualification(); return; }
      const style = getComputedStyle(bar);
      const milliseconds = value => parseFloat(value) * (value.trim().endsWith('ms') ? 1 : 1000);
      const durations = style.transitionDuration.split(',').map(milliseconds);
      const delays = style.transitionDelay.split(',').map(milliseconds);
      const wait = Math.max(0, ...durations.map((duration, i) => duration + delays[i % delays.length]));
      qualificationTimer = setTimeout(() => {
        qualificationTimer = 0;
        pendingQualificationBurst = false;
        if (visible(qualified) && visible(knob) && !motion.matches && !document.hidden) {
          clearConfetti();
          burstConfetti(knob);
        }
      }, wait ? wait + 32 : 0);
    });
  }

  // Read-only adapter: native CartForm remains responsible for cart mutations.
  const liveCartRefreshEnabled = true;
  let refreshRevision = 0;
  let refreshTimer = 0;
  let refreshController = null;

  async function refreshTracker(revision) {
    const block = document.querySelector('.shopify-app-block[id*="x_away_from_free_shipping"]');
    const section = block?.closest('.shopify-section');
    if (!section?.id.startsWith('shopify-section-') || typeof AbortController === 'undefined') return;
    const controller = new AbortController();
    refreshController = controller;
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', section.id.slice('shopify-section-'.length));
      const options = { method: 'GET', credentials: 'same-origin', cache: 'no-store', signal: controller.signal };
      const cartUrl = new URL(`${window.Shopify?.routes?.root || '/'}cart.js`, window.location.origin);
      const [response, cartResponse] = await Promise.all([fetch(url, options), fetch(cartUrl, options)]);
      if (!response.ok || !cartResponse.ok) return;
      const [html, cart] = await Promise.all([response.text(), cartResponse.json()]);
      if (!Number.isSafeInteger(cart?.total_price) || cart.total_price < 0 ||
        !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || !/^[A-Z]{3}$/.test(cart.currency)) return;
      if (controller.signal.aborted || revision !== refreshRevision || !block.isConnected) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = parsed.getElementById(section.id);
      const nextBlock = parsed.getElementById(block.id);
      if (!nextSection?.contains(nextBlock)) return;
      const wrapper = nextBlock.querySelector('.xaway-wrapper');
      if (!wrapper && (nextBlock.children.length || nextBlock.textContent.trim())) return;
      if (cart.item_count > 0) {
        if (!wrapper) return;
        const source = wrapper.querySelector('.app-cart-amount-price');
        const remaining = wrapper.querySelector('.xaway-free-shipping-text');
        const success = wrapper.querySelector('.sucess-free-shipping-text');
        const progress = wrapper.querySelector('.xaway-progress');
        const configId = `xaway_freeshipping-${wrapper.dataset.id}`;
        const configNode = parsed.getElementById(configId) || document.getElementById(configId);
        if (!source || !remaining || !success || !configNode) return;
        const config = JSON.parse(configNode.textContent);
        const threshold = Math.round(Number(config.min_purchase) * 100);
        if (!Number.isSafeInteger(threshold) || threshold <= 0) return;
        const qualifies = cart.total_price >= threshold;
        source.textContent = new Intl.NumberFormat('en-US', { style: 'currency', currency: cart.currency })
          .format(Math.max(0, threshold - cart.total_price) / 100);
        const show = (node, visible) => {
          node.classList.toggle('powerup-b', visible);
          node.classList.toggle('powerup-n', !visible);
        };
        show(remaining, !qualifies);
        show(success, qualifies);
        if (progress) {
          const enabled = value => value === true || value === 'true';
          show(progress, qualifies ? enabled(config.pb_qualifie) || enabled(config.icon_before_qualifies) :
            config.display_type !== 'text_only');
          const bar = progress.querySelector('.xaway-progress-bar');
          if (bar) bar.style.width = `${Math.min(100, cart.total_price / threshold * 100)}%`;
        }
      } else {
        nextBlock.replaceChildren();
      }
      const bars = [...nextBlock.querySelectorAll('.xaway-progress-bar')];
      if (bars.some(bar => !/^\d+(?:\.\d+)?%$/.test(bar.style.width) ||
        parseFloat(bar.style.width) > 100)) return;
      // Keep only inert app settings; never execute returned scripts or reload assets.
      nextBlock.querySelectorAll('script:not([type="application/json"]), link').forEach(node => node.remove());
      const currentBars = [...block.querySelectorAll('.xaway-progress-bar')];
      const targets = bars.map((bar, i) => {
        const target = bar.style.width;
        const previous = currentBars[i]?.getBoundingClientRect().width;
        if (previous !== undefined) bar.style.width = `${previous}px`;
        // Updated markup should not repeat the page-load entrance animation.
        progressStates.set(bar, { bar, width: bar.style.width, frame: 0 });
        return { bar, target };
      });
      block.replaceChildren(...nextBlock.childNodes);
      sync();
      requestAnimationFrame(() => targets.forEach(({ bar, target }) => {
        if (bar.isConnected) bar.style.width = target;
      }));
    } catch {
      // Keep the last display on timeout, network failure or unexpected markup.
    } finally {
      clearTimeout(timeout);
      if (refreshController === controller) refreshController = null;
    }
  }

  function queueTrackerRefresh() {
    if (!liveCartRefreshEnabled) return;
    refreshRevision += 1;
    refreshController?.abort();
    clearTimeout(refreshTimer);
    // Cancel an old celebration, then let the next confirmed threshold crossing rearm it.
    cancelQualificationBurst();
    const revision = refreshRevision;
    refreshTimer = setTimeout(() => refreshTracker(revision), 80);
  }
  document.addEventListener('on:cart:change', queueTrackerRefresh);

  function sync() {
    syncProgress();
    decorateBenefits();
    states.forEach((state, source) => {
      if (!source.isConnected) {
        previousValues.set(state.key, state.displayed);
        stop(state);
        states.delete(source);
      }
    });
    document.querySelectorAll(selector).forEach(update);
    checkQualification();
  }
  const observer = new MutationObserver(records => {
    // Ignore all presentation writes, including animation text and sizing styles.
    if (records.some(record => {
      const element = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      if (element?.closest('.rmcf-shipping-amount-visual')) return false;
      if (record.type === 'attributes') {
        // Animation-variable writes must not feed back into the observer each frame.
        if (record.attributeName === 'style' && progressStates.has(element)) {
          return progressStates.get(element).width !== element.style.width;
        }
        return element?.matches('.xaway-free-shipping-text, .sucess-free-shipping-text, .xaway-progress, .xaway-progress-bar');
      }
      return true;
    })) sync();
  });
  sync();
  observer.observe(document.body, { childList: true, subtree: true, characterData: true,
    attributes: true, attributeFilter: ['class', 'style'] });
  function finish() {
    cancelQualificationBurst();
    progressStates.forEach(finishProgress);
    states.forEach(state => {
      stop(state);
      state.displayed = state.final;
      state.visual.textContent = state.text;
    });
  }
  window.addEventListener('resize', () => { clearConfetti(); progressStates.forEach(finishProgress); });
  motion.addEventListener('change', () => { if (motion.matches) finish(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) finish(); });
})();
